import { Amendment } from '@fnModels/llc';
import { LLCTransaction } from '@models/llc-mortgages';

export interface AmendmentModalData {
	transaction: LLCTransaction;
}

export interface AmendmentMapping {
	ownership: Ownership;
	section: Section;
	field: Field;
}

export enum Ownership {
	LENDER = 'lender',
	SHARED = 'shared',
	EXCEPTION = 'exception',
}

export enum Section {
	PROPERTY = 'property',
	MATTER = 'matter',
	MATTER_MORTGAGE = 'matterMortgage',
	CLIENT = 'client',
	MATTER_TITLE_INSURANCE = 'titleInsurance',
}

export enum Field {
	CLIENT = 'client',
	ADDRESS = 'address',
	CLIENT_PRIORITY = 'client_priority',
	CLIENT_COMPANY_NAME = 'client_companyName',
	CLIENT_FIRSTNAME = 'client_firstName',
	CLIENT_MIDDLENAME = 'client_middleName',
	CLIENT_LASTNAME = 'client_lastName',
	CLIENT_PHONE = 'client_phone',
	CLIENT_BUSINESS_PHONE = 'client_businessPhone',
	CLIENT_SPOUSAL_FIRST_NAME = 'client_spouse_firstname',
	CLIENT_SPOUSAL_LAST_NAME = 'client_spouse_lastname',
	ADDRESS_UNIT = 'address_unit',
	ADDRESS_STREET_NUMBER = 'address_number',
	ADDRESS_STREET1 = 'address_street1',
	ADDRESS_STREET2 = 'address_street2',
	ADDRESS_CITY = 'address_city',
	ADDRESS_PROVINCE = 'address_province',
	ADDRESS_COUNTRY = 'address_country',
	ADDRESS_POSTALCODE = 'address_postcode',
	PROPERTY_CONDO_LEVEL = 'property_condoLevel',
	PROPERTY_CONDO_UNIT_NUMBER = 'property_condoUnitNumber',
	PROPERTY_OCCUPANCY_TYPE = 'property_occupancyType',
	PROPERTY_TYPE_NUMBER_OF_UNITS = 'property_numberOfUnits',
	PROPERTY_CONDO_PLAN_NUMBER = 'property_condoPlanNumber',
	PROPERTY_LENDER_COLLECTS_PROPERTY_TAXES = 'property_lenderCollectsPropertyTaxes',
	MORTGAGE_NUMBER = 'mortgage_number',
	LENDER_REMARKS = 'lender_remarks',
	MORTGAGE_MATTER_REGISTERED_AMOUNT = 'mortgage_matter_registered_amount',
	MORTGAGE_MATTER_REGISTERED_AMOUNT_TEXT = 'mortgage_matter_registered_amount_text',
	MORTGAGE_MATTER_REGISTERED_AMOUNT_TEXT_FR = 'mortgage_matter_registered_amount_text_fr',
	MORTGAGE_MATTER_PAYMENT_FREQUENCY = 'mortgage_matter_payment_frequency',
	MORTGAGE_MATTER_PAYMENTS_DUE = 'mortgage_matter_payments_due',
	MORTGAGE_MATTER_PAYMENTS_DUE_FR = 'mortgage_matter_payments_due_fr',
	MORTGAGE_MATTER_INTEREST_ADJ_DATE = 'mortgage_matter_interest_adj_date',
	MORTGAGE_MATTER_INTEREST_ADJ_DATE_TEXT = 'mortgage_matter_interest_adj_date_text',
	MORTGAGE_MATTER_INTEREST_ADJ_DATE_TEXT_FR = 'mortgage_matter_interest_adj_date_text_fr',
	MORTGAGE_MATTER_FIRST_PAYMENT_DATE = 'mortgage_matter_first_payment_date',
	MORTGAGE_MATTER_FIRST_PAYMENT_DATE_TEXT = 'mortgage_matter_first_payment_date_text',
	MORTGAGE_MATTER_FIRST_PAYMENT_DATE_TEXT_FR = 'mortgage_matter_first_payment_date_text_fr',
	MORTGAGE_MATTER_MATURITY_DATE = 'mortgage_matter_maturity_date',
	MORTGAGE_MATTER_MATURITY_DATE_TEXT = 'mortgage_matter_maturity_date_text',
	MORTGAGE_MATTER_PAYMENT_AMOUNT = 'mortgage_matter_payment_amount',
	MORTGAGE_MATTER_CHARGE_TERM = 'mortgage_matter_charge_term',
	MORTGAGE_MATTER_LAST_PAYMENT_DATE = 'mortgage_matter_last_payment_date',
	MORTGAGE_MATTER_PRINCIPLE_AND_INTEREST = 'mortgage_matter_principle_interest',
	MORTGAGE_MATTER_INTEREST_RATE = 'mortgage_matter_interest_rate',
	MORTGAGE_MATTER_INTEREST_RATE_TYPE = 'mortgage_matter_interest_rate_type',

	MORTGAGE_MATTER_BRIDGE_AMOUNT = 'mortgage_matter_bridge_amount',
	MORTGAGE_MATTER_BRIDGE_INTEREST_RATE = 'mortgage_matter_bridge_interest_rate',
	MORTGAGE_MATTER_BRIDGE_INTEREST_ADJ_DATE = 'mortgage_matter_bridge_interest_adj_date',
	MORTGAGE_MATTER_BRIDGE_FIRST_PAYMENT_DATE = 'mortgage_matter_bridge_first_payment_date',
	MORTGAGE_MATTER_BRIDGE_MATURITY_DATE = 'mortgage_matter_bridge_maturity_date',
	PROPERTY_ESTATE_TYPE = 'property_estateType',
	MORTGAGE_LENDER_REFERENCE_NUMBER = 'mortgage_lender_reference_number',
	PROPERTY_MORTGAGE_PRIORITY = 'property_mortgagePriority',
	PROPERTY_OTHER_ESTATE_TYPE_DESCRIPTION = 'property_otherEstateTypeDescription',
	MATTER_PURCHASE_PRICE = 'matter_purchase_price',
	MATTER_CLOSING_DATE = 'matter_closing_date',

	// new ones
	MORTGAGE_MATTER_TERM = 'mortgage_matter_term',
	MORTGAGE_MATTER_LOAN_AMOUNT = 'mortgage_matter_loan_amount',
	MORTGAGE_MATTER_NET_ADVANCED = 'mortgage_matter_net_advanced',
	MORTGAGE_MATTER_MORTGAGE_INSURER = 'mortgage_matter_mortgage_insurer',
	MORTGAGE_MATTER_NET_ADVANCED_AMOUNTS = 'mortgage_matter_net_advanced_amounts',
	MORTGAGE_MATTER_ASSIGNMENT_OF_RENTS = 'mortgage_matter_assignment_of_rents',
	MORTGAGE_MATTER_TAXES = 'mortgage_matter_taxes',
	MORTGAGE_MATTER_OTHER = 'mortgage_matter_other',
	MORTGAGE_MATTER_REG_PAYMENTS = 'mortgage_matter_reg_payments',
	MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT = 'mortgage_matter_payment_amount_text',
	MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT_FR = 'mortgage_matter_payment_amount_text_fr',
	MORTGAGE_MATTER_INC_ABOVE_BELOW_PRIME = 'mortgage_matter_incremental_above_below_prime',
	MORTGAGE_MATTER_INTEREST_CALCULATED = 'mortgage_matter_interest_calc',
	MORTGAGE_MATTER_INTEREST_CALCULATED_FR = 'mortgage_matter_interest_calc_fr',
	MORTGAGE_MATTER_LOAN_FEES_APPRAISAL = 'mortgage_matter_loan_fees_appraisal',
	MORTGAGE_MATTER_LOAN_FEES_HOLD_BACK = 'mortgage_matter_loan_fees_holdback',
	MORTGAGE_MATTER_LOAN_FEES_INSURANCE_APPLICATION = 'mortgage_matter_loan_fees_ins_application',
	MORTGAGE_MATTER_LOAN_FEES_INSURANCE_PREMIUM = 'mortgage_matter_loan_fees_ins_premium',
	MORTGAGE_MATTER_LOAN_FEES_LENDER = 'mortgage_matter_loan_fees_lender',
	MORTGAGE_MATTER_LOAN_FEES_MISC = 'mortgage_matter_loan_fees_misc',
	MORTGAGE_MATTER_LOAN_FEES_WIRE = 'mortgage_matter_loan_fees_wire_fee',
	MORTGAGE_MATTER_LOAN_FEES_INS_PREMIUM_TAX = 'mortgage_matter_loan_fees_ins_premium_tax',
	MORTGAGE_MATTER_LOAN_FEES_BUYDOWN = 'mortgage_matter_loan_fees_buydown',
	MORTGAGE_MATTER_LOAN_FEES_BRIDGE = 'mortgage_matter_loan_fees_bridge',
	MORTGAGE_MATTER_LOAN_FEES_CREDIT_LINE = 'mortgage_matter_loan_fees_credit_line',
	MORTGAGE_MATTER_LOAN_FEES_TAX_HOLDBACK = 'mortgage_matter_loan_fees_tax_holdback',
	MORTGAGE_MATTER_LOAN_FEES_INTERNAL_PAYOUT = 'mortgage_matter_loan_fees_payout',
	MORTGAGE_MATTER_LOAN_FEES_REBATE = 'mortgage_matter_loan_fees_rebate',
	MORTGAGE_MATTER_LOAN_FEES_FCT_FEE = 'mortgage_matter_loan_fct_fee',
	MORTGAGE_CASH_BACK_AMOUNT = 'mortgage_cash_back',
	MORTGAGE_RATE_EXPIRY_DATE = 'mortgage_rate_expiry_date',
	MORTGAGE_RATIO_INDICATOR = 'mortgage_ratio_indicator',
	MORTGAGE_SOLICITOR_COLSED = 'mortgage_solicitor_closed',
	MORTGAGE_CONSTRUCTION_MORTGAGE = 'mortgage_construction_mortgage',
	MORTGAGE_BASE_RATE = 'mortgage_base_rate',
	MORTGAGE_ACTUAL_MORTGAGE_RATE = 'mortgage_actual_mortgage_rate',
	MORTGAGE_REG_INTEREST_RATE_TEXT = 'mortgage_reg_interest_mortgage_rate_text',
	MORTGAGE_REG_INTEREST_RATE_TEXT_FR = 'mortgage_reg_interest_mortgage_rate_fr',
	MORTGAGE_MATTER_PRODUCT = 'mortgage_matter_product',
	MORTGAGE_MATTER_CALC_PERIOD_CODE = 'mortgage_matter_calc_period_code',
	MORTGAGE_MATTER_CALC_PERIOD_TEXT = 'mortgage_matter_calc_period_text',
	MORTGAGE_MATTER_CALC_PERIOD_TEXT_FR = 'mortgage_matter_calc_period_fr',
	MORTGAGE_UNSECURED_DEBT_AMOUNT = 'mortgage_matter_unsecureddebt_amount',
	MORTGAGE_UNSECURED_DEBT_CREDITOR = 'mortgage_matter_unsecureddebt_creditor',
	MORTGAGE_UNSECURED_DEBT_ACTION = 'mortgage_matter_unsecureddebt_action',

	SOLICITOR_CONDITIONS_SOLICITOR_CONDITION_CLAUSE = 'solicitorConditions_solicitorCondition_clause',
	SOLICITOR_CONDITIONS_SOLICITOR_CONDITION_CLAUSE_SEQUENCE = 'solicitorConditions_solicitorCondition_clauseSequence',

	MORTGAGE_ILAS = 'mortgage_ilas',
	MORTGAGE_ILAS_ILA_FIRSTNAME = 'mortgage_ilas_ila_firstname',
	MORTGAGE_ILAS_ILA_MIDDLENAME = 'mortgage_ilas_ila_middlename',
	MORTGAGE_ILAS_ILA_LASTNAME = 'mortgage_ilas_ila_lastname',

	MORTGAGE_OWNING_BRANCH_NAME = 'mortgageowningbranch_name',
	MORTGAGE_OWNING_BRANCH_STREET_1 = 'mortgageowningbranch_address_streetaddress1',
	MORTGAGE_OWNING_BRANCH_STREET_2 = 'mortgageowningbranch_address_streetaddress2',
	MORTGAGE_OWNING_BRANCH_POSTALCODE = 'mortgageowningbranch_address_postcode',
	MORTGAGE_OWNING_BRANCH_CITY = 'mortgageowningbranch_address_city',
	MORTGAGE_OWNING_BRANCH_FAX = 'mortgageowningbranch_fax',
	MORTGAGE_OWNING_BRANCH_PHONE = 'mortgageowningbranch_phone',
	MORTGAGE_OWNING_BRANCH_TRANSIT_NUMBER = 'mortgageowningbranch_transit_number',
	MORTGAGE_OWNING_BRANCH_FIRSTNAME = 'mortgageowningbranch_firstname',
	MORTGAGE_OWNING_BRANCH_LASTNAME = 'mortgageowningbranch_lastname',

	MORTGAGE_CENTRE_NAME = 'mortgageCentre_name',
	MORTGAGE_CENTRE_ADDRESS_STREET_1 = 'mortgageCentre_address_street1',
	MORTGAGE_CENTRE_ADDRESS_STREET_2 = 'mortgageCentre_address_street2',
	MORTGAGE_CENTRE_ADDRESS_POSTALCODE = 'mortgageCentre_address_postcode',
	MORTGAGE_CENTRE_ADDRESS_CITY = 'mortgageCentre_address_city',
	MORTGAGE_CENTRE_FAX = 'mortgageCentre_fax',
	MORTGAGE_CENTRE_PHONE = 'mortgageCentre_phone',
	MORTGAGE_CENTRE_EMAIL = 'mortgageCentre_email',
	MORTGAGE_CENTRE_TRANSIT_NUMBER = 'mortgage_centre_transit_number',
	MORTGAGE_CENTRE_FIRSTNAME = 'mortgageCentre_firstname',
	MORTGAGE_CENTRE_LASTNAME = 'mortgageCentre_lastname',

	FUNDING = 'funding',
	FUNDING_PAYMENT_METHOD = 'funding_payment_method',
	FUNDING_TRANSIT_NUMBER = 'funding_transit_number',
	FUNDING_BANK_NUMBER = 'funding_bank_number',
	FUNDING_RFF_REQUIRED = 'funding_rff_required',
	FUNDING_ACCOUNT_NUMBER = 'funding_account_number',
}

export const AddressFields = [
	Field.ADDRESS_UNIT,
	Field.ADDRESS_STREET_NUMBER,
	Field.ADDRESS_STREET1,
	Field.ADDRESS_STREET2,
	Field.ADDRESS_CITY,
	Field.ADDRESS_PROVINCE,
	Field.ADDRESS_COUNTRY,
	Field.ADDRESS_POSTALCODE,
];

export const Mappings: { [id: string]: AmendmentMapping } = {
	'/solicitorconditions/solicitorcondition/clause': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.SOLICITOR_CONDITIONS_SOLICITOR_CONDITION_CLAUSE,
	},
	'/solicitorconditions/solicitorcondition/clausesequence': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.SOLICITOR_CONDITIONS_SOLICITOR_CONDITION_CLAUSE_SEQUENCE,
	},
	'/mortgage/ilas': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_ILAS,
	},
	'/mortgage/ilas/ila/firstname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_ILAS_ILA_FIRSTNAME,
	},
	'/mortgage/ilas/ila/middlename': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_ILAS_ILA_MIDDLENAME,
	},
	'/mortgage/ilas/ila/lastname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_ILAS_ILA_LASTNAME,
	},
	'/mortgagecentre/name': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_NAME,
	},
	'/mortgagecentre/address/streetaddress1': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_ADDRESS_STREET_1,
	},
	'/mortgagecentre/address/streetaddress2': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_ADDRESS_STREET_2,
	},
	'/mortgagecentre/address/postalcode': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_ADDRESS_POSTALCODE,
	},
	'/mortgagecentre/address/city': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_ADDRESS_CITY,
	},
	'/mortgagecentre/fax': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_FAX,
	},
	'/mortgagecentre/phone': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_PHONE,
	},
	'/mortgagecentre/transitnumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_TRANSIT_NUMBER,
	},
	'/mortgagecentre/firstname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_FIRSTNAME,
	},
	'/mortgagecentre/lastname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CENTRE_LASTNAME,
	},
	'/mortgageowningbranch/name': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_NAME,
	},
	'/mortgageowningbranch/address/streetaddress1': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_STREET_1,
	},
	'/mortgageowningbranch/address/streetaddress2': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_STREET_2,
	},
	'/mortgageowningbranch/address/postalcode': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_POSTALCODE,
	},
	'/mortgageowningbranch/address/city': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_CITY,
	},
	'/mortgageowningbranch/fax': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_FAX,
	},
	'/mortgageowningbranch/phone': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_PHONE,
	},
	'/mortgageowningbranch/transitnumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_TRANSIT_NUMBER,
	},
	'/mortgageowningbranch/firstname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_FIRSTNAME,
	},
	'/mortgageowningbranch/lastname': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_OWNING_BRANCH_LASTNAME,
	},
	'/properties/property/mortgagepriority': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.PROPERTY_MORTGAGE_PRIORITY,
	},
	'/mortgagors/mortgagor/unsecureddebts/unsecureddebt/amount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_UNSECURED_DEBT_AMOUNT,
	},
	'/mortgagors/mortgagor/unsecureddebts/unsecureddebt/creditor': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_UNSECURED_DEBT_CREDITOR,
	},
	'//mortgagors/mortgagor/unsecureddebts/unsecureddebt/action': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_UNSECURED_DEBT_ACTION,
	},
	'/mortgage/mortgageproduct': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PRODUCT,
	},
	'/mortgage/calculationperiod/code': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_CALC_PERIOD_CODE,
	},
	'/mortgage/calculationperiod/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_CALC_PERIOD_TEXT,
	},
	'/mortgage/calculationperiod/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_CALC_PERIOD_TEXT_FR,
	},
	'/mortgage/interestrate/baserate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_BASE_RATE,
	},
	'/mortgage/interestrate/actualmortgagerate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_ACTUAL_MORTGAGE_RATE,
	},
	'/mortgage/constructionmortgage': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CONSTRUCTION_MORTGAGE,
	},
	'/mortgage/cashbackamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_CASH_BACK_AMOUNT,
	},
	'/mortgage/ratioindicator': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_RATIO_INDICATOR,
	},
	'/mortgage/registeredinterestratetext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_REG_INTEREST_RATE_TEXT_FR,
	},
	'/mortgage/registeredinterestratetext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_REG_INTEREST_RATE_TEXT,
	},
	'/funding': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING,
	},
	'/funding/paymentmethod': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING_PAYMENT_METHOD,
	},
	'/funding/transitnumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING_TRANSIT_NUMBER,
	},
	'/funding/banknumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING_BANK_NUMBER,
	},
	'/funding/rffrequired': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING_RFF_REQUIRED,
	},
	'/funding/accountnumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.FUNDING_ACCOUNT_NUMBER,
	},
	'/mortgageloanfees/appraisalfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_APPRAISAL,
	},
	'/mortgageloanfees/holdbackfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_HOLD_BACK,
	},
	'/mortgageloanfees/insuranceapplicationfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_INSURANCE_APPLICATION,
	},
	'/mortgageloanfees/insurancepremium': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_INSURANCE_PREMIUM,
	},
	'/mortgageloanfees/lenderfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_LENDER,
	},
	'/mortgageloanfees/miscellaneousfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_MISC,
	},
	'/mortgageloanfees/wirefee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_WIRE,
	},
	'/mortgageloanfees/taxoninsurancepremium': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_INS_PREMIUM_TAX,
	},
	'/mortgageloanfees/buydownatcommitmentfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_BUYDOWN,
	},
	'/mortgageloanfees/bridgeloanapplicationfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_BRIDGE,
	},
	'/mortgageloanfees/lineofcredit': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_CREDIT_LINE,
	},
	'/mortgageloanfees/taxholdback': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_TAX_HOLDBACK,
	},
	'/mortgageloanfees/internalmortgagepayout': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_INTERNAL_PAYOUT,
	},
	'/mortgageloanfees/rebate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_REBATE,
	},
	'/mortgageloanfees/fctfee': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_FEES_FCT_FEE,
	},
	'/remarks': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.LENDER_REMARKS,
	},
	'/mortgage/mortgagenumber': {
		ownership: Ownership.SHARED,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_NUMBER,
	},
	'/mortgagors/mortgagor': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.CLIENT,
	},
	'/guarantors/guarantor': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.CLIENT,
	},
	'/mortgagors/mortgagor/address': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS,
	},
	'/guarantors/guarantor/address': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS,
	},
	// this is going to be ignored so setting the section to mortgage instead of client.
	'/mortgagors/mortgagor/priorityindicator': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.CLIENT_PRIORITY,
	},
	'/mortgagors/mortgagor/companyname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_COMPANY_NAME,
	},
	'/mortgagors/mortgagor/firstname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_FIRSTNAME,
	},
	'/mortgagors/mortgagor/middlename': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_MIDDLENAME,
	},
	'/mortgagors/mortgagor/lastname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_LASTNAME,
	},
	'/mortgagors/mortgagor/phone': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_PHONE,
	},
	'/mortgagors/mortgagor/businessphone': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_BUSINESS_PHONE,
	},
	'/mortgagors/mortgagor/address/unitnumber': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_UNIT,
	},
	'/mortgagors/mortgagor/address/streetnumber': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET_NUMBER,
	},
	'/mortgagors/mortgagor/address/streetaddress1': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET1,
	},
	'/mortgagors/mortgagor/address/streetaddress2': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET2,
	},
	'/mortgagors/mortgagor/address/city': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_CITY,
	},
	'/mortgagors/mortgagor/address/country': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS_COUNTRY,
	},
	'/mortgagors/mortgagor/address/province': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS_PROVINCE,
	},
	'/mortgagors/mortgagor/address/postalcode': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_POSTALCODE,
	},
	'/mortgagors/mortgagor/spousefirstname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_SPOUSAL_FIRST_NAME,
	},
	'/mortgagors/mortgagor/spouselastname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_SPOUSAL_LAST_NAME,
	},
	'/guarantors/guarantor/companyname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_COMPANY_NAME,
	},
	'/guarantors/guarantor/firstname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_FIRSTNAME,
	},
	'/guarantors/guarantor/middlename': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_MIDDLENAME,
	},
	'/guarantors/guarantor/lastname': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_LASTNAME,
	},
	'/guarantors/guarantor/phone': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_PHONE,
	},
	'/guarantors/guarantor/businessphone': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.CLIENT_BUSINESS_PHONE,
	},
	'/guarantors/guarantor/address/unitnumber': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_UNIT,
	},
	'/guarantors/guarantor/address/streetnumber': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET_NUMBER,
	},
	'/guarantors/guarantor/address/streetaddress1': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET1,
	},
	'/guarantors/guarantor/address/streetaddress2': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_STREET2,
	},
	'/guarantors/guarantor/address/city': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_CITY,
	},
	'/guarantors/guarantor/address/country': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS_COUNTRY,
	},
	'/guarantors/guarantor/address/province': {
		ownership: Ownership.EXCEPTION,
		section: Section.CLIENT,
		field: Field.ADDRESS_PROVINCE,
	},
	'/guarantors/guarantor/address/postalcode': {
		ownership: Ownership.SHARED,
		section: Section.CLIENT,
		field: Field.ADDRESS_POSTALCODE,
	},
	'/properties/property/condolevel': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_CONDO_LEVEL, // not used
	},
	'/properties/property/condounitnumber': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_CONDO_UNIT_NUMBER, // not used
	},
	'/properties/property/occupancytype': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_OCCUPANCY_TYPE,
	},
	'/properties/property/numberofunits': {
		ownership: Ownership.SHARED,
		section: Section.MATTER_TITLE_INSURANCE,
		field: Field.PROPERTY_TYPE_NUMBER_OF_UNITS,
	},
	'/properties/property/condoplannumber': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_CONDO_PLAN_NUMBER, // not used
	},
	'/properties/property/lendercollectspropertytaxes': {
		ownership: Ownership.SHARED,
		section: Section.MATTER_MORTGAGE,
		field: Field.PROPERTY_LENDER_COLLECTS_PROPERTY_TAXES,
	},
	'/properties/property/estatetype': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_ESTATE_TYPE,
	},
	'/lenderreferencenumber': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_LENDER_REFERENCE_NUMBER,
	},
	'/properties/property/otherestatetypedescription': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.PROPERTY_OTHER_ESTATE_TYPE_DESCRIPTION,
	},
	'/properties/property/address/unitnumber': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_UNIT,
	},
	'/properties/property/address/streetnumber': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_STREET_NUMBER,
	},
	'/properties/property/address/streetaddress1': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_STREET1,
	},
	'/properties/property/address/streetaddress2': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_STREET2,
	},
	'/properties/property/address/city': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_CITY,
	},
	'/properties/property/address/country': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_COUNTRY,
	},
	'/properties/property/address/province': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_PROVINCE,
	},
	'/properties/property/address/postalcode': {
		ownership: Ownership.SHARED,
		section: Section.PROPERTY,
		field: Field.ADDRESS_POSTALCODE,
	},
	'/mortgage/closingdate': {
		ownership: Ownership.SHARED,
		section: Section.MATTER,
		field: Field.MATTER_CLOSING_DATE,
	},
	'mortgage/purchaseprice': {
		ownership: Ownership.SHARED,
		section: Section.MATTER,
		field: Field.MATTER_PURCHASE_PRICE,
	},
	'/mortgage/mortgageterm': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_TERM,
	},
	'/mortgage/registeredamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_REGISTERED_AMOUNT,
	},
	'/mortgage/registrationamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_REGISTERED_AMOUNT,
	},
	'/mortgage/registeredamounttext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_REGISTERED_AMOUNT_TEXT,
	},
	'/mortgage/registeredamounttext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_REGISTERED_AMOUNT_TEXT_FR,
	},
	'/mortgage/registrationpaymentfreq': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PAYMENT_FREQUENCY,
	},
	'/mortgage/registrationpaymentfrequency': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PAYMENT_FREQUENCY,
	},
	'/mortgage/mortgagepayments/mortgagepayment/paymentdateandperiodtext/textfr':
		{
			ownership: Ownership.LENDER,
			section: Section.MATTER_MORTGAGE,
			field: Field.MORTGAGE_MATTER_PAYMENTS_DUE_FR,
		},
	'/mortgage/mortgagepayments/mortgagepayement/paymentdateandperiodtext/textfr':
		{
			ownership: Ownership.LENDER,
			section: Section.MATTER_MORTGAGE,
			field: Field.MORTGAGE_MATTER_PAYMENTS_DUE_FR,
		},
	'/mortgage/mortgagepayments/mortgagepayment/paymentdateandperiodtext/texten':
		{
			ownership: Ownership.LENDER,
			section: Section.MATTER_MORTGAGE,
			field: Field.MORTGAGE_MATTER_PAYMENTS_DUE,
		},
	'/mortgage/mortgagepayments/mortgagepayement/paymentdateandperiodtext/texten':
		{
			ownership: Ownership.LENDER,
			section: Section.MATTER_MORTGAGE,
			field: Field.MORTGAGE_MATTER_PAYMENTS_DUE,
		},
	'/mortgage/interestadjustmentdatetext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_ADJ_DATE_TEXT,
	},
	'/mortgage/interestadjustmentdatetext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_ADJ_DATE_TEXT_FR,
	},
	'/mortgage/interestadjustmentdate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_ADJ_DATE,
	},
	'/mortgage/mortgagepayments/mortgagepayment/firstpaymentdatetext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_FIRST_PAYMENT_DATE_TEXT,
	},
	'/mortgage/mortgagepayments/mortgagepayment/firstpaymentdatetext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_FIRST_PAYMENT_DATE_TEXT_FR,
	},
	'/mortgage/mortgagepayments/mortgagepayment/firstpaymentdate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_FIRST_PAYMENT_DATE,
	},
	'/mortgage/maturitydate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_MATURITY_DATE,
	},
	'/mortgage/standardchargetermtext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_CHARGE_TERM,
	},
	'/mortgage/maturitydatetext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_MATURITY_DATE_TEXT,
	},
	'/mortgage/mortgagepayments/mortgagepayment/paymentamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PRINCIPLE_AND_INTEREST,
	},
	'/mortgage/calculationperiod/calculationperiodtext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_CALCULATED_FR,
	},
	'/mortgage/calculationperiod/calculationperiodtext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_CALCULATED,
	},
	'/mortgage/interestrate/incrementabovebelowprime': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INC_ABOVE_BELOW_PRIME,
	},
	'/mortgage/interestrate/fixedrate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_RATE,
	},
	'/mortgage/interestrate/interestratetype': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_INTEREST_RATE_TYPE,
	},
	'/mortgage/mortgagepayments/mortgagepayment/total': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_REG_PAYMENTS,
	},
	'/mortgage/mortgagepayments/mortgagepayment/paymentamounttext/textvalue/text':
		{
			ownership: Ownership.LENDER,
			section: Section.MATTER_MORTGAGE,
			field: Field.MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT,
		},
	'/mortgage/mortgagepayments/mortgagepayment/paymentamounttext/textfr': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT_FR,
	},
	'/mortgage/mortgagepayments/mortgagepayment/paymentamounttext/texten': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_PAYMENT_AMOUNT_TEXT,
	},
	'/mortgage/mortgagepayments/mortgagepayment/insuranceamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_OTHER,
	},
	'/mortgage/mortgagepayments/mortgagepayment/propertytaxamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_TAXES,
	},
	'/mortgage/assignmentofrents': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_ASSIGNMENT_OF_RENTS,
	},
	'/mortgage/mortgageamountadvanced': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_NET_ADVANCED,
	},
	'/mortgage/mortgageinsurer': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_MORTGAGE_INSURER,
	},
	'/mortgageloanfees/netmortgageadvance': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_NET_ADVANCED_AMOUNTS,
	},
	'/mortgage/totalloanamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_LOAN_AMOUNT,
	},
	'/mortgage/bridgeloan/loanamount': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_BRIDGE_AMOUNT,
	},
	'/mortgage/bridgeloan/interestrate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_BRIDGE_INTEREST_RATE,
	},
	'/mortgage/bridgeloan/interestadjustmentdate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_BRIDGE_INTEREST_ADJ_DATE,
	},
	'/mortgage/bridgeloan/firstpaymentdate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_BRIDGE_FIRST_PAYMENT_DATE,
	},
	'/mortgage/bridgeloan/maturitydate': {
		ownership: Ownership.LENDER,
		section: Section.MATTER_MORTGAGE,
		field: Field.MORTGAGE_MATTER_BRIDGE_MATURITY_DATE,
	},
};

export interface AmendmentVM {
	allowedToOverride: boolean;
	override: boolean;
	amendment: Amendment;
	action: 'add' | 'remove' | 'modify';
	previousValue: string;
	newValue: string;
	mapping?: AmendmentMapping;
	closerClientId?: string;
	extraLabel?: string;
}

export interface Status {
	updateCloser: boolean;
	acceptAmendments: boolean;
	getLatestData: boolean;
	updateStatus: boolean;
}
